// Step 2: the Slater-Koster fit set-up (the desktop's SetupPage): orbitals per element, spin, the bands to fit, the
// model and the optimiser on the left; the fitted band window and the starting values / bounds on the right; Run fit.
import { Alert, Anchor, Badge, Button, Card, Checkbox, Grid, Group, Menu, MultiSelect, NumberInput, Select, SimpleGrid, Stack, Table, Tabs,
  Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ErrorBox, notifyError, notifyOk } from "../../components/Errors";
import { CancelButton, JobProgress } from "../../components/Jobs";
import { Plot } from "../../components/Plot";
import { RefLinks } from "../../components/RefLinks";
import { limitError } from "../../components/RunCard";
import { convergenceFigure, windowFigure } from "../../components/SkfitterPlots";
import { post, type Job, type ProjectDetail } from "../../lib/api";
import { useLiveJob, useMe } from "../../lib/hooks";
import { useReferences, tagged } from "../../lib/references";
import { CharacterTab } from "./CharacterTab";
import { NeighboursTab } from "./LatticeViews";
import { applyOverrides, applySaved, cfgSignature, coverageText, DEFAULT_START, defaultCfg, FIT_FILE, listParamSets, readParamFile, readTable,
  setupView, startValues, tableFromModel, tableFromStart, type Cfg, type DftResult, type FitOptions, type ModelResult,
  magneticPossible, type ParamSetInfo, type SkForms, type SkMemory, type StartValues, type TableRow } from "../../lib/skfitter";

export function SetupStep({ project, forms, dft, dftJobId, mem, update, remember, onBack, onFitted }: {
  project: ProjectDetail; forms: SkForms; dft: DftResult; dftJobId: string; mem: SkMemory; update: (m: Partial<SkMemory>) => void;
  remember: (m: Partial<SkMemory>) => void; onBack: () => void; onFitted: (jobId: string) => void;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const refs = useReferences();                        // "[n]" beside the fit and SOC (skfitter/skfitter_references)
  const elements = dft.meta.elements;
  const usable = (c?: Cfg) => !!c && JSON.stringify(Object.keys(c.orbital_config).sort()) === JSON.stringify([...elements].sort());
  const cfg: Cfg = usable(mem.cfg) ? mem.cfg! : defaultCfg(forms, dft);
  const opts: FitOptions = { ...forms.fit_options, ...(mem.opts ?? {}) };
  const maxShells = Math.min(16, Number((project.limits ?? me?.limits ?? {})["skfitter.max_shells"] ?? 16));
  const setCfg = (p: Partial<Cfg>) => update({ cfg: { ...cfg, ...p } });
  const setOpts = (p: Partial<FitOptions>) => update({ opts: { ...opts, ...p } });

  const [debounced] = useDebouncedValue(JSON.stringify([cfg, opts]), 300);
  const check = useQuery({
    queryKey: ["sk-setup", dftJobId, debounced],
    queryFn: () => { const [c, o] = JSON.parse(debounced); return setupView(dftJobId, c, o); },
    placeholderData: (p) => p,
  });
  const c = check.data;

  // -- the parameter table (valid only for the set-up it was built for) -----------------------------------------
  const sig = cfgSignature(cfg);
  const rows = mem.table_sig === sig ? mem.table ?? [] : [];
  const setRows = (r: TableRow[]) => update({ table: applyOverrides(r, mem.overrides), table_sig: sig });
  const { job: modelJob } = useLiveJob(mem.model_job);
  const model = modelJob?.status === "succeeded" ? (modelJob.result as ModelResult) : undefined;
  const modelFits = model && cfgSignature(model.cfg) === sig;
  const applied = useRef<string | null>(mem.table_sig === sig && mem.table?.length ? mem.model_job ?? null : null);
  const pendingSaved = useRef<Record<string, number> | null>(null);

  // -- starting values from a published parameter set (or the defaults: DFT band centres) ------------------------
  const startSet = mem.start_set ?? DEFAULT_START;
  const sets = useQuery({ queryKey: ["sk-paramsets", elements.join(",")], queryFn: () => listParamSets(elements), staleTime: 60_000 });
  const [startInfo, setStartInfo] = useState<StartValues | null>(null);
  const [startBusy, setStartBusy] = useState(false);
  const applyStart = async (setId: string, m: ModelResult, jobId: string) => {
    if (setId === DEFAULT_START) { setStartInfo(null); setRows(tableFromModel(m)); return; }
    setStartBusy(true);
    try {
      const sv = await startValues(jobId, setId);
      setStartInfo(sv);
      update({ table: applyOverrides(tableFromStart(m, sv), mem.overrides), table_sig: sig, start_set: setId });
    } catch (e) {
      notifyError(e);
      setRows(tableFromModel(m));
    } finally {
      setStartBusy(false);
    }
  };
  const chooseStart = (setId: string) => {
    update({ start_set: setId });
    if (model && modelFits && modelJob) void applyStart(setId, model, modelJob.id);
    else if (!modelBusy && !build.isPending) build.mutate();          // applied as soon as the model is built
  };

  useEffect(() => {
    if (model && modelFits && modelJob && applied.current !== modelJob.id) {
      applied.current = modelJob.id;
      setRows(tableFromModel(model));
      if (pendingSaved.current) {
        const [next, n] = applySaved(tableFromModel(model), pendingSaved.current, model.env);
        pendingSaved.current = null;
        setRows(next);
        notifyOk(`${n} starting values taken from the saved fit.`);
      } else if (startSet !== DEFAULT_START) {
        void applyStart(startSet, model, modelJob.id);
      }
    }
  });
  const [tab, setTab] = useState<string | null>("window");
  const build = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: "skfitter.model", project_id: project.id,
      params: { dft: dft.dft, elements, cfg } }),
    onSuccess: (j) => {
      applied.current = null;
      remember({ model_job: j.id, cfg, opts, start_set: startSet });
      setTab("table");                    // a quick model job may finish before "busy" is ever seen
    },
  });
  const modelBusy = modelJob && (modelJob.status === "queued" || modelJob.status === "running");
  // an illustrative project arrives with its set-up but no parameter table: build the model once, so the starting
  // values (parameter set + the project's fixed values) are in the table before "Run fit"
  const autoBuilt = useRef(false);
  useEffect(() => {
    if (mem.example && !autoBuilt.current && !mem.model_job && !rows.length && !build.isPending) {
      autoBuilt.current = true;
      build.mutate();
    }
  });
  useEffect(() => { if (modelBusy) setTab("table"); }, [modelBusy]);

  const fitFiles = project.files.filter((f) => f.status === "ready" && FIT_FILE.test(f.name));
  const takeStart = async (fileId: string) => {
    const r = await readParamFile(fileId).catch((e) => { notifyError(e); return null; });
    if (!r) return;
    if (!r.ok || !r.params) return notifyError(new Error(r.error));
    if (!rows.length) {                                   // not built for this set-up yet: build, then apply
      pendingSaved.current = r.params;
      build.mutate();
      return;
    }
    const [next, n] = applySaved(rows, r.params, model?.env);
    setRows(next);
    notifyOk(`${n} starting values taken from the saved fit.`);
  };
  const table = readTable(rows);

  // -- run / load -----------------------------------------------------------------------------------------------
  const [runId, setRunId] = useState<string | undefined>();
  const { job: running } = useLiveJob(runId);
  useEffect(() => {
    if (running?.status === "succeeded") { setRunId(undefined); onFitted(running.id); }
  }, [running, onFitted]);
  const common = { dft: dft.dft, elements, cfg, seed: mem.seed };
  const run = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: "skfitter.fit", project_id: project.id, params: {
      ...common, opts, initial: rows.length ? table.initial : {}, bounds: rows.length ? table.bounds : {} } }),
    onSuccess: (j) => { setRunId(j.id); remember({ cfg, opts, table: rows, table_sig: sig, start_set: startSet }); qc.invalidateQueries({ queryKey: ["me"] }); },
  });
  const load = useMutation({
    mutationFn: (param_file: string) => post<Job>("/jobs", { kind: "skfitter.load", project_id: project.id,
      params: { ...common, param_file } }),
    onSuccess: (j) => { setRunId(j.id); remember({ cfg, opts }); },
  });
  const blocked = !!c?.orbital_error || !!c?.limit || !!c?.size_limit || !!c?.opts_error || Object.keys(table.errors).length > 0;

  if (running && (running.status === "queued" || running.status === "running" || running.status === "failed" || running.status === "cancelled")) {
    const conv = (running.result as { convergence?: { n: number[]; rmse: number[] } } | null)?.convergence;
    const fig = conv?.n.length ? convergenceFigure(conv.n, conv.rmse) : null;
    const active = running.status === "queued" || running.status === "running";
    return (
      <Card withBorder mt="md">
        <Stack>
          <Text fw={600} size="lg">{running.kind === "skfitter.load" ? "Loading the saved fit …" : `Fitting ${mem.seed} …`}</Text>
          <JobProgress job={running} />
          {fig && <Plot data={fig.data} layout={fig.layout} height={320} />}
          {running.error && <ErrorBox error={new Error(running.error)} />}
          <Group justify="space-between">
            <Text size="xs" c="dimmed">The fitter's detailed output is in the calculation's log.</Text>
            {active ? <CancelButton job={running} label={running.kind === "skfitter.fit" ? "Stop and keep the best so far" : "Cancel"} />
              : <Button variant="default" onClick={() => setRunId(undefined)}>Back to the set-up</Button>}
          </Group>
        </Stack>
      </Card>
    );
  }

  const m = dft.meta;
  return (
    <Grid gutter="lg" mt="md">
      <Grid.Col span={{ base: 12, lg: 6 }}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {m.formula} · {m.spacegroup} · {m.magnetic ? "spin-polarised" : "spin-degenerate"} DFT · {m.soc ? "with" : "without"} spin-orbit
            in the DFT run. Choose what the Slater-Koster tight-binding model contains.<RefLinks refs={tagged(refs.data, "skfitter", "skfitter-fit")} />
          </Text>

          <Section title="Orbitals per element">
            {dft.elements.map((e) => (
              <ElementOrbitals key={e.symbol} forms={forms} symbol={e.symbol} count={e.count}
                value={cfg.orbital_config[e.symbol] ?? []}
                onChange={(o) => setCfg({ orbital_config: { ...cfg.orbital_config, [e.symbol]: o } })} />
            ))}
            {c?.orbital_error && <Text size="sm" c="red">{c.orbital_error}</Text>}
            {!c?.orbital_error && c?.orbital_warning && <Text size="sm" c="orange">⚠ {c.orbital_warning}</Text>}
            {c?.size_limit && <ErrorBox error={limitError(c.size_limit)} />}
          </Section>

          <Section title="Spin">
            <Group gap={0} wrap="nowrap">
              <Checkbox label="Include spin-orbit coupling (λ per element and p / d / f family)" checked={cfg.include_soc}
                onChange={(e) => setCfg({ include_soc: e.currentTarget.checked })} />
              <RefLinks refs={tagged(refs.data, "skfitter", "skfitter-soc")} />
            </Group>
            <Text size="xs" c="dimmed">{m.soc ? "The DFT calculation included spin-orbit coupling."
              : "The DFT calculation had no spin-orbit coupling; switching it on here doubles the basis and adds λ parameters (needed for AHC / SHC)."}</Text>
            <Text size="sm" fw={500} mt="xs">Magnetic orbitals (exchange splitting δ per family)</Text>
            <Text size="xs" c="dimmed">{m.magnetic
              ? "The DFT bands are spin-split, so the model is spin-doubled. Tick the families that carry the exchange splitting (one parameter delta_spin_<element>_<family> each); with the orbitals split by symmetry (e.g. d_t2g, d_eg, pz) a subshell can have its own."
              : magneticPossible(m)
                ? "Non-collinear DFT with magnetic moments (MAGMOM): tick the families that carry the exchange splitting to make the model magnetic (moments along z, up or down per site as in MAGMOM)."
                : "The DFT bands are spin-degenerate: no exchange splitting to fit."}</Text>
            {magneticPossible(m) && dft.elements.map((e) => (
              <Group key={e.symbol} gap="md">
                <Text size="sm" fw={600} w={40}>{e.symbol}</Text>
                {[...["s", "p", "d", "f"], ...symmetrySubshells(forms, cfg.orbital_config[e.symbol] ?? [])].map((f) => (
                  <Tooltip key={f} openDelay={400} label={f.length > 1
                    ? `Own exchange splitting for ${f} (delta_spin_${e.symbol}_${f}); the rest of the ${familyOf(forms, f)} shell keeps the family's`
                    : `One exchange splitting for all ${f} orbitals (delta_spin_${e.symbol}_${f})`}>
                    <Checkbox size="xs" label={f} checked={(cfg.magnetic_orbitals[e.symbol] ?? []).includes(f)}
                      onChange={(ev) => {
                        const cur = new Set(cfg.magnetic_orbitals[e.symbol] ?? []);
                        if (ev.currentTarget.checked) cur.add(f); else cur.delete(f);
                        setCfg({ magnetic_orbitals: { ...cfg.magnetic_orbitals, [e.symbol]: [...cur] } });
                      }} />
                  </Tooltip>
                ))}
              </Group>
            ))}
          </Section>

          <Section title="Bands to fit">
            <SimpleGrid cols={2} spacing="xs">
              <Num label="Bands below E_F" value={cfg.n_bands_below} min={0} max={60} tip="Bands counted downwards from the Fermi level (per spin unless SOC doubles them)"
                onChange={(v) => setCfg({ n_bands_below: v })} />
              <Num label="Bands above E_F" value={cfg.n_bands_above} min={0} max={60} tip="Bands counted upwards from the Fermi level"
                onChange={(v) => setCfg({ n_bands_above: v })} />
            </SimpleGrid>
            <Text size="xs" c="dimmed">{!c ? "" : c.window == null
              ? "The number of electrons is not in the DFT data - the window is worked out when the model is built."
              : c.window.count ? `DFT bands ${c.window.first}–${c.window.last} (${c.window.count} bands, ${c.window.lo.toFixed(2)} to ${c.window.hi.toFixed(2)} eV around E_F; last occupied band ${c.window.vbm})`
                : "No bands selected."}</Text>
            <Tooltip multiline w={380} label={"Uses the orbital projections: at each k-point only DFT states with at least half of their weight on the "
              + "model's orbitals are fitted, so the window follows those bands. Leaves out the vacuum (nearly-free-electron) states of 2D layers "
              + "and bands of orbitals the model does not have, e.g. the σ bands for a pz model. Needs a non-magnetic material with projections."}>
              <Checkbox label="Fit only DFT states of the model's orbitals (skips vacuum states and other bands)"
                checked={!!cfg.model_states_only} onChange={(e) => setCfg({ model_states_only: e.currentTarget.checked })} />
            </Tooltip>
            <Num label="Scissor: shift the DFT conduction bands (eV)" value={cfg.scissor ?? 0} min={-5} max={5} step={0.05} decimals={3}
              tip={"Every DFT band above the last occupied one is moved by this much before fitting, so the fit reproduces a corrected gap "
                + "(e.g. the measured gap, or one from a hybrid functional) and keeps its physical parameters. Needs a gap in the DFT bands; "
                + "0 = fit the DFT bands as they are. The band window plot shows the shifted bands."}
              onChange={(v) => setCfg({ scissor: v })} />
          </Section>

          <Section title="Model">
            <Checkbox label="Non-orthogonal basis (adds overlap parameters s_*)" checked={cfg.non_orthogonal}
              onChange={(e) => setCfg({ non_orthogonal: e.currentTarget.checked })} />
            <SimpleGrid cols={2} spacing="xs">
              <Num label="Neighbour shells" value={cfg.n_neighbors} min={1} max={maxShells}
                tip={`Number of neighbour shells (distinct neighbour distances) whose hopping integrals are fitted - up to ${maxShells} on your plan; see the “Neighbours included” tab`}
                onChange={(v) => setCfg({ n_neighbors: v })} />
              <Num label="Symmetry tolerance" value={cfg.symprec} min={1e-6} max={0.1} step={1e-4} decimals={6}
                tip="Tolerance of the symmetry analysis (equivalent atoms)" onChange={(v) => setCfg({ symprec: v })} />
            </SimpleGrid>
            <Select size="xs" label="Hopping integrals vs distance" allowDeselect={false} value={cfg.hopping_mode ?? "decay"}
              onChange={(v) => v && setCfg({ hopping_mode: v as "decay" | "shell" })}
              data={[{ value: "decay", label: "Decay law - one integral per bond type, V₀ (d₀/r)^η" },
                { value: "shell", label: "Per neighbour shell - an independent integral for each shell" }]} />
            <Text size="xs" c="dimmed">{(cfg.hopping_mode ?? "decay") === "shell"
              ? "Each shell n gets its own integrals (v_dds_0, v_dds_1, …): more freedom and more parameters, no η fitted. "
                + "Tip: start from a saved decay-law fit (Starting values from a saved fit …) - its integrals are converted to each shell's distance."
              : "One set of integrals at the nearest-neighbour distance d₀, scaled to longer bonds by (d₀/r)^η."}</Text>
            <Tooltip multiline w={340} label={"One energy per orbital group leaves out on-site terms that the site symmetry allows: the mixing "
              + "of two groups of the same symmetry type (d E₁ / E₂ on a trigonal site), of orbital families on a site without inversion "
              + "(s – d_z², p_z – d_z²), and the crystal field of a shell the site splits further. Each adds one parameter e_mix_* per "
              + "independent term and class of equivalent sites (the count appears in the parameter table)."}>
              <MultiSelect size="xs" label="On-site mixing allowed by the site symmetry" placeholder={cfg.onsite_mixing?.length ? "" : "none"}
                clearable data={Object.keys(cfg.orbital_config).filter((e) => cfg.orbital_config[e]?.length)}
                value={cfg.onsite_mixing ?? []} onChange={(v) => setCfg({ onsite_mixing: v })} />
            </Tooltip>
            <Checkbox label="Fit the distance-decay exponent η of the hoppings (for more than one shell)" checked={cfg.fit_eta}
              disabled={(cfg.hopping_mode ?? "decay") === "shell"}
              onChange={(e) => setCfg({ fit_eta: e.currentTarget.checked })} />
            <SimpleGrid cols={2} spacing="xs">
              <Num label="Fixed η" value={cfg.fixed_eta} min={0.5} max={8} step={0.1} decimals={2}
                tip={(cfg.hopping_mode ?? "decay") === "shell"
                  ? "Per shell: η scales an integral only for bonds strained off its shell distance, V_n (r_n/r)^η (e.g. strain in NEGF)"
                  : "η used when it is not fitted"}
                onChange={(v) => setCfg({ fixed_eta: v })} />
              <div>
                <Tooltip multiline w={300} label="The fitter estimates the valence electrons occupying the model orbitals; untick to set the number by hand (it decides which band is the last occupied one).">
                  <Checkbox mt={4} size="xs" label="Tight-binding electron count: automatic" checked={cfg.tb_nelect == null}
                    onChange={(e) => setCfg({ tb_nelect: e.currentTarget.checked ? null : 8 })} />
                </Tooltip>
                {cfg.tb_nelect != null && <NumberInput mt={4} size="xs" aria-label="Tight-binding electrons" value={cfg.tb_nelect} min={0} max={500}
                  onChange={(v) => typeof v === "number" && setCfg({ tb_nelect: v })} />}
              </div>
            </SimpleGrid>
          </Section>

          <Section title="Optimiser">
            <Select size="xs" label="Method" allowDeselect={false} value={opts.method} onChange={(v) => v && setOpts({ method: v as FitOptions["method"] })}
              data={[{ value: "ls", label: "Least squares, multi-start - fast, needs reasonable starting values" },
                { value: "de", label: `Differential evolution - global, slow, no starting values needed${me && !(project.limits ?? me.limits)["skfitter.differential_evolution"] ? " (not in your plan)" : ""}` }]} />
            <SimpleGrid cols={3} spacing="xs">
              <Num label="Starts (least squares)" value={opts.n_starts} min={1} max={200} disabled={opts.method !== "ls"}
                tip="Multi-start least squares: number of starts (the first one is your starting guess)" onChange={(v) => setOpts({ n_starts: v })} />
              <Num label="Generations (DE)" value={opts.maxiter} min={1} max={100000} disabled={opts.method !== "de"}
                tip="Differential evolution generations" onChange={(v) => setOpts({ maxiter: v })} />
              <Num label="Population factor (DE)" value={opts.popsize} min={1} max={200} disabled={opts.method !== "de"}
                tip="Population size per parameter" onChange={(v) => setOpts({ popsize: v })} />
              <Num label="k-point stride" value={opts.k_stride} min={1} max={100}
                tip="Use every n-th k-point in the global stage; the polish and the reported errors use all" onChange={(v) => setOpts({ k_stride: v })} />
              <Num label="Polish: max evaluations" value={opts.refine_max_nfev} min={10} max={100000} step={10}
                onChange={(v) => setOpts({ refine_max_nfev: v })} />
              <Num label="Random seed" value={opts.seed ?? 0} min={0} max={2 ** 31 - 1} tip="Random seed (reproducible fits)"
                onChange={(v) => setOpts({ seed: v })} />
            </SimpleGrid>
            <Checkbox label="Finish with a bounded least-squares polish on all k-points" checked={opts.refine}
              onChange={(e) => setOpts({ refine: e.currentTarget.checked })} />
            <Group gap="xs" align="flex-end">
              <Checkbox label="Emphasise the high-symmetry k-points" checked={opts.add_weight_at_symmetry_points} mb={6}
                onChange={(e) => setOpts({ add_weight_at_symmetry_points: e.currentTarget.checked })} />
              <Num label="weight" value={opts.symmetry_weight} min={1} max={1000} w={80} onChange={(v) => setOpts({ symmetry_weight: v })} />
              <Num label="width (k-points)" value={opts.symmetry_width} min={0} max={20} w={110} onChange={(v) => setOpts({ symmetry_width: v })} />
            </Group>
            <Group gap="xs" align="flex-end">
              <Checkbox label="Emphasise the bands near E_F" checked={opts.add_weight_at_fermi_bands} mb={6}
                onChange={(e) => setOpts({ add_weight_at_fermi_bands: e.currentTarget.checked })} />
              <Num label="weight" value={opts.fermi_weight} min={1} max={1000} w={80} onChange={(v) => setOpts({ fermi_weight: v })} />
              <Num label="window (eV)" value={opts.fermi_window} min={0.05} max={10} step={0.1} decimals={2} w={110} onChange={(v) => setOpts({ fermi_window: v })} />
            </Group>
            {c?.limit && <ErrorBox error={limitError(c.limit)} />}
            {c?.opts_error && <Text size="sm" c="red">{c.opts_error}</Text>}
          </Section>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 6 }}>
        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="window">Bands used in the fit</Tabs.Tab>
            <Tabs.Tab value="character">Orbital character</Tabs.Tab>
            <Tabs.Tab value="neighbours">Neighbours included</Tabs.Tab>
            <Tabs.Tab value="table">Starting values and bounds</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="character" pt="xs">
            {tab === "character" && <CharacterTab jobId={dftJobId} cfg={cfg} />}
          </Tabs.Panel>
          <Tabs.Panel value="neighbours" pt="xs">
            {tab === "neighbours" && <NeighboursTab jobId={dftJobId} cfg={cfg} />}
          </Tabs.Panel>
          <Tabs.Panel value="window" pt="xs">
            {c?.plot ? <WindowPlot plot={c.plot} count={c.window?.count ?? 0} /> : <Text size="sm" c="dimmed">…</Text>}
          </Tabs.Panel>
          <Tabs.Panel value="table" pt="xs">
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                {modelBusy ? "Building the model (reading the DFT data, neighbour search) …"
                  : rows.length && model && modelFits ? `${model.info.n_params} free parameters · Hamiltonian ${model.info.total_dim}×${model.info.total_dim} · ${model.info.n_target_bands} bands × ${model.info.nk} k-points · ${model.info.n_pairs} neighbour pairs · ${model.info.spacegroup} · TB electrons ${model.info.tb_nelect}${model.info.moments ? ` · moments ${model.info.moments}` : ""}`
                  : mem.table?.length && mem.table_sig !== sig ? "The set-up changed - press “Build model” again to refresh the parameter list."
                  : c?.basis ? `Basis: ${c.basis} spatial orbitals, Hamiltonian dimension ${c.dim}. Press “Build model” to see the parameters, edit their starting values and bounds.`
                  : "Press “Build model” to see the parameters, edit their starting values and bounds."}
              </Text>
              {modelBusy && <JobProgress job={modelJob!} />}
              {modelJob?.status === "failed" && <ErrorBox error={new Error(modelJob.error)} />}
              <Group gap="xs">
                <Button size="xs" onClick={() => build.mutate()} loading={build.isPending} disabled={!!modelBusy || !!c?.orbital_error || !!c?.size_limit}>Build model</Button>
                <Button size="xs" variant="default" disabled={!model || !modelFits || startBusy}
                  onClick={() => model && modelJob && void applyStart(startSet, model, modelJob.id)}>
                  {startSet === DEFAULT_START ? "Reset to defaults" : "Reset to the set's values"}</Button>
                <FileMenu label="Starting values from a saved fit …" files={fitFiles} onPick={takeStart} />
              </Group>
              <ErrorBox error={build.error} />
              <StartSetPicker sets={sets.data ?? []} value={startSet} onChange={chooseStart} busy={startBusy || !!modelBusy || build.isPending}
                elements={elements} />
              {startSet !== DEFAULT_START && startInfo && startInfo.set === startSet && <StartInfo info={startInfo} />}
              {startSet !== DEFAULT_START && !(model && modelFits) && !modelBusy &&
                <Text size="xs" c="dimmed">Applied when the model is built (“Build model”).</Text>}
              {rows.length > 0 && <ParamTable rows={rows} errors={table.errors} onChange={setRows} />}
            </Stack>
          </Tabs.Panel>
        </Tabs>
        <ErrorBox error={run.error ?? load.error} />
        <Group justify="space-between" mt="md">
          <Button variant="default" onClick={onBack}>← Back</Button>
          <Group gap="xs">
            <FileMenu label="Use saved parameters (no refit)" files={fitFiles} onPick={(id) => load.mutate(id)}
              tip="Rebuild this model and load a saved <seedname>_fit.json / .txt (from the web or the desktop program)" />
            <Button onClick={() => run.mutate()} loading={run.isPending} disabled={blocked || !!modelBusy}>Run fit ▶</Button>
          </Group>
        </Group>
        {!rows.length && <Text size="xs" c="dimmed" ta="right" mt={4}>Without a built parameter table the fit starts from the default values (on-site energies from the DFT projections).</Text>}
      </Grid.Col>
    </Grid>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <Text fw={600} size="sm" mb={6}>{title}</Text>
      <Stack gap={6}>{children}</Stack>
    </div>
  );
}

function Num({ label, value, onChange, min, max, step, decimals, tip, disabled, w }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; decimals?: number;
  tip?: string; disabled?: boolean; w?: number;
}) {
  const input = (
    <NumberInput size="xs" label={label} value={value} min={min} max={max} step={step ?? 1} decimalScale={decimals ?? 0}
      allowDecimal={!!decimals} disabled={disabled} w={w} onChange={(v) => typeof v === "number" && onChange(v)} />
  );
  return tip ? <Tooltip label={tip} multiline w={280} openDelay={400}>{input}</Tooltip> : input;
}

function ElementOrbitals({ forms, symbol, count, value, onChange }: {
  forms: SkForms; symbol: string; count: number; value: string[]; onChange: (o: string[]) => void;
}) {
  return (
    <Card withBorder p="xs">
      <Group justify="space-between" mb={4}>
        <Text size="sm"><b>{symbol}</b> · {count} atom{count !== 1 ? "s" : ""} in the cell</Text>
        <Select size="xs" w={150} placeholder="(preset)" value={null} aria-label={`${symbol} preset`}
          data={Object.keys(forms.presets)} onChange={(v) => v && onChange([...forms.presets[v]])} />
      </Group>
      <Table withRowBorders={false} verticalSpacing={2} horizontalSpacing={4}>
        <Table.Tbody>
          {forms.orbital_layout.map((row) => (
            <Table.Tr key={row.family}>
              <Table.Td w={70}><Text size="xs" c="dimmed">{row.family}</Text></Table.Td>
              {row.orbitals.map((o) => (
                <Table.Td key={o}>
                  <Tooltip label={forms.orbital_tips[o] ?? o} openDelay={400}>
                    <Checkbox size="xs" label={o} aria-label={`${symbol} ${o}`} checked={value.includes(o)}
                      onChange={(e) => onChange(e.currentTarget.checked ? [...value, o] : value.filter((x) => x !== o))} />
                  </Tooltip>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function WindowPlot({ plot, count }: { plot: Parameters<typeof windowFigure>[0]; count: number }) {
  const fig = useMemo(() => windowFigure(plot, count), [plot, count]);
  return <Plot data={fig.data} layout={fig.layout} height={420} />;
}

function FileMenu({ label, files, onPick, tip }: { label: string; files: { id: string; name: string }[]; onPick: (id: string) => void; tip?: string }) {
  const button = <Button size="xs" variant="default" disabled={!files.length}>{label}</Button>;
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>{tip ? <Tooltip label={files.length ? tip : "No saved fit file (…_fit.json / _fit.txt) in this project yet."} multiline w={300}>{button}</Tooltip> : button}</Menu.Target>
      <Menu.Dropdown mah={300} style={{ overflowY: "auto" }}>
        {files.map((f) => <Menu.Item key={f.id} onClick={() => onPick(f.id)}>{f.name}</Menu.Item>)}
      </Menu.Dropdown>
    </Menu>
  );
}

const SHORT_GROUP: Record<string, string> = {
  "On-site energy": "on-site", Hopping: "hopping", Overlap: "overlap", "Spin-orbit": "SOC", "Exchange splitting": "exchange",
  "Distance decay": "η",
};

function ParamTable({ rows, errors, onChange }: { rows: TableRow[]; errors: Record<string, string>; onChange: (r: TableRow[]) => void }) {
  const set = (i: number, p: Partial<TableRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const withSrc = rows.some((r) => r.src);
  const groups = [...new Set(rows.map((r) => r.group))];
  const [only, setOnly] = useState<string>("all");
  const shown = only === "all" || !groups.includes(only) ? null : only;
  return (
    <Stack gap={6}>
    <Group gap={6}>
      <Text size="xs" c="dimmed">Show:</Text>
      <Badge size="sm" variant={shown === null ? "filled" : "light"} style={{ cursor: "pointer" }} onClick={() => setOnly("all")}>
        All ({rows.length})</Badge>
      {groups.map((g) => (
        <Badge key={g} size="sm" color="gray" variant={shown === g ? "filled" : "light"} style={{ cursor: "pointer" }}
          onClick={() => setOnly(g)}>{g} ({rows.filter((r) => r.group === g).length})</Badge>
      ))}
    </Group>
    <Table.ScrollContainer minWidth={520}>
      <Table stickyHeader verticalSpacing={2} striped>
        <Table.Thead>
          <Table.Tr><Table.Th>Group</Table.Th><Table.Th>Parameter</Table.Th><Table.Th>Start</Table.Th><Table.Th>Lower</Table.Th><Table.Th>Upper</Table.Th>
            {withSrc && <Table.Th>From</Table.Th>}</Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r, i) => (shown && r.group !== shown ? null :
            <Table.Tr key={r.key}>
              <Table.Td><Text size="xs" c="dimmed" title={r.group} style={{ whiteSpace: "nowrap" }}>{SHORT_GROUP[r.group] ?? r.group}</Text></Table.Td>
              <Table.Td><Text size="xs" ff="monospace">{r.key}</Text>{errors[r.key] && <Text size="xs" c="red">{errors[r.key]}</Text>}</Table.Td>
              {(["start", "lo", "hi"] as const).map((k) => (
                <Table.Td key={k}>
                  <TextInput size="xs" w={76} value={r[k]} aria-label={`${r.key} ${k}`} error={!!errors[r.key]}
                    onChange={(e) => set(i, { [k]: e.currentTarget.value })} />
                </Table.Td>
              ))}
              {withSrc && <Table.Td><Badge size="xs" variant={r.src === "set" ? "light" : "outline"} color={r.src === "set" ? "teal" : "gray"}>
                {r.src === "set" ? "set" : "default"}</Badge></Table.Td>}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
    </Stack>
  );
}


const KIND_LABEL: Record<ParamSetInfo["kind"], string> = {
  dftb: "Slater-Koster tables", nist: "on-site only", harrison: "universal formulas", nrl: "NRL tight-binding",
};

function StartSetPicker({ sets, value, onChange, busy, elements }: {
  sets: ParamSetInfo[]; value: string; onChange: (v: string) => void; busy: boolean; elements: string[];
}) {
  const data = [
    { value: DEFAULT_START, label: "Defaults (on-site energies from the DFT band centres)" },
    ...sets.map((s) => {
      const miss = s.coverage?.missing ?? [];
      const cov = !elements.length ? "" : miss.length === elements.length ? " - none of the elements"
        : miss.length ? ` - no ${miss.join(", ")}` : "";
      return { value: s.id, label: `${s.name} (${KIND_LABEL[s.kind] ?? s.kind})${cov}`, disabled: miss.length === elements.length && elements.length > 0 };
    }),
  ];
  const current = sets.find((s) => s.id === value);
  return (
    <>
      <Select size="xs" label="Starting values and bounds from" allowDeselect={false} value={value} data={data}
        onChange={(v) => v && onChange(v)} disabled={busy} aria-label="Starting values from"
        description="Published parameter sets for each element and orbital; the set's energies are shifted to the DFT band centres, bounds are set around its values." />
      {current?.description && <Text size="xs" c="dimmed">{current.description}</Text>}
    </>
  );
}

function StartInfo({ info }: { info: StartValues }) {
  return (
    <Alert variant="light" color="teal" p="xs">
      <Stack gap={4}>
        <Text size="xs"><b>{info.name}</b>: {coverageText(info.coverage)} taken from the set; the rest keep their defaults.</Text>
        {info.notes.map((n) => <Text key={n} size="xs">{n}</Text>)}
        {info.citation && <Text size="xs" c="dimmed">Please cite: {info.citation}{info.licence ? ` · ${info.licence}` : ""}</Text>}
        {info.set.startsWith("dftb-") && <Text size="xs" c="dimmed">
          Parameter sets from <Anchor size="xs" href="https://dftb.org/parameters" target="_blank" rel="noreferrer">DFTB.org</Anchor>.</Text>}
      </Stack>
    </Alert>
  );
}


/** The family (s, p, d, f) of an orbital choice, from the form's orbital layout ("d sets" -> d). */
function familyOf(forms: SkForms, o: string): string {
  const row = forms.orbital_layout.find((r) => r.orbitals.includes(o));
  return row ? row.family.charAt(0) : o;
}

/** The symmetry subshells of an element's orbital set (d_t2g, pz, dz2, ...): each may get its own exchange splitting. */
function symmetrySubshells(forms: SkForms, orbitals: string[]): string[] {
  return orbitals.filter((o) => familyOf(forms, o) !== o);
}
